import React, { FC, useState } from 'react';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { ActionPriceList, PriceListAction } from 'models/action';
import { useTranslation } from 'react-i18next';
import ActionItem from './action-item.component';
import SortableHeaderLabel from 'components/shared/sortable-header-label/sortable-header-label.component';
import { OrderBy } from 'models/search-result';
import {
    ACTION_PRICE_LIST_SORT_FIELDS,
    DEFAULT_ACTION_PRICE_LIST_ORDER,
    sortPriceListActions,
    toggleOrderBy,
} from 'utils/price-list-sort';

type ActionListProps = {
    actionPriceList?: ActionPriceList;
    onDiscountedPriceChange: (actionDiscountedPrice: { id?: string; discountedPrice: string }) => void;
    onCustomerSpecificTiotandvardPriceChange: (actionTiotandvardPrice: {
        id?: string;
        customerSpecificTiotandvardPrice: string;
    }) => void;
    filters: { freetext: string; category: string };
    currency: string;
    isTioTandvardPricingForLabEnabled: boolean;
};
const ActionListComponent: FC<ActionListProps> = ({
    actionPriceList,
    onDiscountedPriceChange,
    onCustomerSpecificTiotandvardPriceChange,
    filters,
    currency,
    isTioTandvardPricingForLabEnabled,
}: ActionListProps) => {
    const { t: translate } = useTranslation(['common', 'client-directory', 'orders', 'actions-materials']);
    const [orderBy, setOrderBy] = useState<OrderBy>(DEFAULT_ACTION_PRICE_LIST_ORDER);

    const handleSortChange = (field: string) => {
        setOrderBy(toggleOrderBy(orderBy, field));
    };

    const filteredActions = (actionPriceList?.actions || [])
        .filter(
            ({ description, actionCode, basePrice }) =>
                description.toLocaleLowerCase().includes(filters.freetext) ||
                actionCode?.includes(filters.freetext) ||
                basePrice?.toString().includes(filters.freetext),
        )
        .filter(({ category }) => category?.includes(filters.category));

    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <SortableHeaderLabel
                                field={ACTION_PRICE_LIST_SORT_FIELDS.ACTION_CODE}
                                label={translate('actions-materials:CODE')}
                                orderBy={orderBy}
                                onSortChange={handleSortChange}
                            />
                        </TableCell>
                        <TableCell>{translate('common:DESCRIPTION')}</TableCell>
                        <TableCell>
                            <SortableHeaderLabel
                                field={ACTION_PRICE_LIST_SORT_FIELDS.CATEGORY}
                                label={translate('orders:CATEGORY')}
                                orderBy={orderBy}
                                onSortChange={handleSortChange}
                            />
                        </TableCell>
                        <TableCell>{translate('client-directory:DATE_ADDED')}</TableCell>
                        <TableCell>{translate('common:BASE_PRICE')}</TableCell>
                        {isTioTandvardPricingForLabEnabled && (
                            <TableCell>{translate('common:TIOTANDVARD_PRICE')}</TableCell>
                        )}
                        {isTioTandvardPricingForLabEnabled && (
                            <TableCell align="right">
                                {translate('client-directory:CLIENT_TIOTANDVARD_PRICE')}
                            </TableCell>
                        )}
                        <TableCell align="right">{translate('client-directory:CLIENT_PRICE')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortPriceListActions(filteredActions, orderBy).map((action: PriceListAction) => (
                        <ActionItem
                            key={action._id}
                            action={action}
                            onDiscountedPriceChange={onDiscountedPriceChange}
                            onCustomerSpecificTiotandvardPriceChange={onCustomerSpecificTiotandvardPriceChange}
                            currency={currency}
                            isTioTandvardPricingForLabEnabled={isTioTandvardPricingForLabEnabled}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default ActionListComponent;
